import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import apiService from '../services/api';
import UserMenu from './UserMenu';

const ProjectReport = ({ userName, onLogout }) => {
  const { identificador } = useParams();
  const navigate = useNavigate();
  const [projetoData, setProjetoData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const reportRef = useRef(null);
  const matrixRef = useRef(null);

  useEffect(() => {
    loadProjetoData();
  }, [identificador]);

  const loadProjetoData = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getProjetoRelatorio(identificador);
      setProjetoData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dados do projeto');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      toast.info('Gerando PDF...');
      
      const element = matrixRef.current;
      if (!element) {
        toast.error('Erro ao gerar PDF: elemento não encontrado');
        return;
      }

      // Configurações para html2canvas - capturar apenas a matriz
      const canvas = await html2canvas(element, {
        scale: 2, // Maior qualidade
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0
      });

      const imgData = canvas.toDataURL('image/png');
      
      // Criar PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;

      // Adicionar primeira página
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Adicionar páginas adicionais se necessário
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Nome do arquivo
      const fileName = `relatorio_${projetoData.nome_projeto.replace(/[^a-zA-Z0-9]/g, '_')}_${projetoData.identificador_unico}.pdf`;
      
      // Download
      pdf.save(fileName);
      toast.success('PDF baixado com sucesso!');
      
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando relatório...</p>
        </div>
      </div>
    );
  }

  if (!projetoData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Projeto não encontrado</h2>
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Voltar ao início
          </button>
        </div>
      </div>
    );
  }

  const { nome_projeto, identificador_unico, data_criacao, solicitante_nome, solicitante_setor, gestor_nome, centro_custo, '5w2h': w2h } = projetoData;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          {/* Título da página à esquerda */}
          <div className="flex-1">
            <h1 className="text-xl font-bold" style={{ color: '#090E3A' }}>Relatório do Projeto</h1>
            <p className="text-sm text-gray-600 font-medium">{nome_projeto} • CEDISA</p>
          </div>
          
          {/* Logo CEDISA centralizada (botão para home) */}
          <div className="flex-1 flex justify-center">
            <button 
              onClick={() => navigate('/')}
              className="hover:opacity-80 transition-opacity duration-200"
            >
              <img src="/cedisa-logo.png" alt="Cedisa Logo" className="h-10 drop-shadow-sm" />
            </button>
          </div>
          
          {/* Nome do usuário na direita */}
          <div className="flex-1 flex justify-end">
            <UserMenu userName={userName} onLogout={onLogout} />
          </div>
        </div>
      </div>

      {/* Aviso Importante */}
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mx-6 mt-4 rounded-r-lg shadow-sm">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700 font-semibold">
              <strong>ATENÇÃO:</strong> Este é o relatório de uma <strong>SOLICITAÇÃO</strong> de projeto. 
              A aprovação desta solicitação <strong>NÃO É GARANTIDA</strong> pela CEDISA. 
              Todas as solicitações passam por análise e aprovação dos responsáveis.
            </p>
          </div>
        </div>
      </div>

      {projetoData.mapa_estrategico && (
        <div className="mt-8 px-8 py-4 bg-orange-50 border-l-4 border-orange-400 rounded shadow">
          <span className="font-bold">Vínculo com o mapa estratégico: </span>
          {projetoData.mapa_estrategico}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8" ref={reportRef}>
        {/* MATRIZ 5W2H - Layout Integrado com Header */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border-2 border-gray-200" ref={matrixRef}>
          
          {/* Header do Projeto Integrado */}
          <div className="bg-gray-100 px-8 py-6 border-b-2 border-gray-200">
            <div className="text-center">
              <h2 className="text-4xl font-bold mb-4" style={{ color: '#090E3A' }}>{nome_projeto}</h2>
              <div className="flex items-center justify-center gap-6 text-lg text-gray-700">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800">ID:</span>
                  <span className="px-3 py-1 bg-blue-200 text-blue-900 rounded-full font-mono text-sm font-semibold">
                    {identificador_unico}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800">Gestor:</span>
                  <span className="text-gray-700">{gestor_nome}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800">Setor:</span>
                  <span className="text-gray-700">{solicitante_setor}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800">Centro de Custo:</span>
                  <span className="text-gray-700">{centro_custo}</span>
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                {(() => {
                  const s = data_criacao || '';
                  const hasTZ = /Z|[+-]\d{2}:?\d{2}$/.test(s);
                  const normalized = hasTZ ? s : `${s.replace(' ', 'T')}Z`;
                  const date = new Date(normalized);
                  return `Criado em: ${new Intl.DateTimeFormat('pt-BR', {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                    timeZone: 'America/Sao_Paulo'
                  }).format(date)}`;
                })()}
              </div>
            </div>
          </div>
          
          {/* Título da Matriz */}
          <div className="text-center py-2 bg-gray-100 border-b border-gray-200">
            <h2 style={{ 
              color: '#090E3A', 
              fontWeight: 'bold',
              fontSize: '1.25rem',
              textAlign: 'center',
              margin: '0',
              padding: '0'
            }}>MATRIZ DO PROJETO</h2>
          </div>

          {/* Grid da Matriz - 5 colunas, 4 linhas */}
          <div className="grid grid-cols-4 gap-0" style={{ gridTemplateRows: 'auto auto auto' }}>
            
            {/* ========== LINHA 1 ========== */}
            
            {/* Col 1 - JUSTIFICATIVAS (Laranja forte) */}
            <div className="bg-orange-200 border-2 border-orange-500 p-6">
              <h3 className="text-lg font-bold text-orange-900 mb-3 pb-2 border-b-2 border-orange-600">JUSTIFICATIVAS</h3>
              <p className="text-sm font-semibold text-orange-800 mb-2">Por quê?</p>
              <p className="text-sm text-orange-900 whitespace-pre-wrap break-words font-medium">{w2h.justificativas}</p>
            </div>

            {/* Col 2 - RESTRIÇÕES (Amarelo forte) */}
            <div className="bg-yellow-200 border-2 border-yellow-500 p-6">
              <h3 className="text-lg font-bold text-yellow-900 mb-3 pb-2 border-b-2 border-yellow-600">RESTRIÇÕES</h3>
              <p className="text-sm font-semibold text-yellow-800 mb-2">Limitações</p>
              <p className="text-sm text-yellow-900 whitespace-pre-wrap break-words font-medium">{w2h.restricoes}</p>
            </div>

            {/* Col 3 - ENTREGÁVEIS (Amarelo forte) */}
            <div className="bg-yellow-200 border-2 border-yellow-500 p-6">
              <h3 className="text-lg font-bold text-yellow-900 mb-3 pb-2 border-b-2 border-yellow-600">ENTREGÁVEIS</h3>
              <p className="text-sm font-semibold text-yellow-800 mb-2">Produtos finais</p>
              <p className="text-sm text-yellow-900 whitespace-pre-wrap break-words font-medium">{w2h.entregaveis}</p>
            </div>

            {/* Col 4 - PRAZOS (Verde forte) */}
            <div className="bg-green-200 border-2 border-green-500 p-6">
              <h3 className="text-lg font-bold text-green-900 mb-3 pb-2 border-b-2 border-green-600">PRAZOS</h3>
              <p className="text-sm font-semibold text-green-800 mb-2">Cronograma</p>
              <p className="text-sm text-green-900 whitespace-pre-wrap break-words font-medium">{w2h.quando}</p>
            </div>

            {/* ========== LINHA 2 ========== */}
            
            {/* Col 1 - OBJETIVOS (Laranja forte) */}
            <div className="bg-orange-200 border-2 border-orange-500 p-6">
              <h3 className="text-lg font-bold text-orange-900 mb-3 pb-2 border-b-2 border-orange-600">OBJETIVOS SMART</h3>
              <p className="text-xs font-semibold text-orange-800 mb-2">S: Específico, M: Mensurável, A: Alcançável, R: Realista, T: Tempo</p>
              <p className="text-sm text-orange-900 whitespace-pre-wrap break-words font-medium">{w2h.objetivos_smart}</p>
            </div>

            {/* Col 2 - REQUISITOS (Amarelo forte) */}
            <div className="bg-yellow-200 border-2 border-yellow-500 p-6">
              <h3 className="text-lg font-bold text-yellow-900 mb-3 pb-2 border-b-2 border-yellow-600">REQUISITOS</h3>
              <p className="text-sm font-semibold text-yellow-800 mb-2">O que os Stakeholders querem</p>
              <p className="text-sm text-yellow-900 whitespace-pre-wrap break-words font-medium">{w2h.requisitos}</p>
            </div>

            {/* Col 3 - RISCOS (Amarelo forte) */}
            <div className="bg-yellow-200 border-2 border-yellow-500 p-6">
              <h3 className="text-lg font-bold text-yellow-900 mb-3 pb-2 border-b-2 border-yellow-600">RISCOS</h3>
              <p className="text-sm font-semibold text-yellow-800 mb-2">O que pode inviabilizar o projeto?</p>
              <p className="text-sm text-yellow-900 whitespace-pre-wrap break-words font-medium">{w2h.riscos}</p>
            </div>

            {/* Col 4 - QUANDO (Verde forte) */}
            <div className="bg-green-200 border-2 border-green-500 p-6">
              <h3 className="text-lg font-bold text-green-900 mb-3 pb-2 border-b-2 border-green-600">QUANDO</h3>
              <p className="text-sm font-semibold text-green-800 mb-2">Estratégia</p>
              <p className="text-sm text-green-900 whitespace-pre-wrap break-words font-medium">{w2h.como}</p>
            </div>

            {/* ========== LINHA 3 ========== */}
            
            {/* Col 1 - BENEFÍCIOS (Laranja forte) */}
            <div className="bg-orange-200 border-2 border-orange-500 p-6">
              <h3 className="text-lg font-bold text-orange-900 mb-3 pb-2 border-b-2 border-orange-600">BENEFÍCIOS</h3>
              <p className="text-sm font-semibold text-orange-800 mb-2">Impacto esperado</p>
              <p className="text-sm text-orange-900 whitespace-pre-wrap break-words font-medium">{w2h.beneficios}</p>
            </div>

            {/* Col 2 - STAKEHOLDERS PRINCIPAIS + DEMAIS (Azul forte) */}
            <div className="bg-blue-200 border-2 border-blue-500 p-6">
              <h3 className="text-lg font-bold text-blue-900 mb-3 pb-2 border-b-2 border-blue-600">STAKEHOLDERS</h3>
              
              <div className="mb-4">
                <p className="text-sm font-semibold text-blue-800 mb-2">Principais Decisores</p>
                <p className="text-sm text-blue-900 whitespace-pre-wrap break-words font-medium">{w2h.stakeholders_principais}</p>
              </div>
              
              <div>
                <p className="text-sm font-semibold text-blue-800 mb-2">Demais Stakeholders</p>
                <p className="text-sm text-blue-900 whitespace-pre-wrap break-words font-medium">{w2h.demais_stakeholders}</p>
              </div>
            </div>

            {/* Col 3 - EQUIPE (Azul forte) */}
            <div className="bg-blue-200 border-2 border-blue-500 p-6">
              <h3 className="text-lg font-bold text-blue-900 mb-3 pb-2 border-b-2 border-blue-600">EQUIPE</h3>
              <p className="text-sm font-semibold text-blue-800 mb-2">Áreas envolvidas</p>
              <p className="text-sm text-blue-900 whitespace-pre-wrap break-words font-medium">{w2h.equipe}</p>
            </div>

            {/* Col 4 - CUSTO (Roxo forte) */}
            <div className="bg-purple-200 border-2 border-purple-500 p-6">
              <h3 className="text-lg font-bold text-purple-900 mb-3 pb-2 border-b-2 border-purple-600">CUSTO</h3>
              <p className="text-sm font-semibold text-purple-800 mb-2">Investimento</p>
              <p className="text-sm text-purple-900 whitespace-pre-wrap break-words font-medium">{w2h.custo}</p>
              <p className="text-xs text-purple-800 whitespace-pre-wrap break-words mt-3 font-semibold"><strong>CC:</strong> {centro_custo}</p>
            </div>

          </div>
        </div>

        {/* Botões de Ação */}
        <div className="flex justify-center gap-4 mt-8">
          <button
            onClick={downloadPDF}
            className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg flex items-center gap-2 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-indigo-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Baixar PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectReport;
